export type DosagePattern =
  | 'daily'
  | 'twice-daily'
  | 'every-x-hours'
  | 'weekly'
  | 'monthly'
  | 'cyclical'
  | 'custom';

export enum MedicationForm {
  Tablet = "Tablet",
  Capsule = "Capsule",
  Injection = "Injection",
  Inhaler = "Inhaler",
  Drops = "Drops",
  Liquid = "Liquid",
  Cream = "Cream",
  Other = "Other"
}

export interface TravelAdjustment {
  originalTime: string;
  adjustedTime: string;
  localDate: string;
  homeDate: string;
  isTransitionDay: boolean;
  isBridgingDose: boolean;
}

/**
 * FHIR Metadata for Medication
 * Contains mappings to standardized healthcare terminology
 */
export interface FhirMetadata {
  rxnormCode?: string;            // RxNorm concept identifier
  rxnormName?: string;            // Standard RxNorm medication name
  fhirStatus?: string;            // FHIR MedicationRequest.status value
  medicationId?: string;          // FHIR Medication resource ID
  medicationRequestId?: string;   // FHIR MedicationRequest resource ID
  snomedCode?: string;            // SNOMED CT code for condition/indication
  snomedName?: string;            // SNOMED CT name for condition/indication
  fhirLastUpdated?: string;       // Timestamp of last FHIR sync
}

/**
 * Schedule for medication administration
 * Maps to FHIR Dosage and Timing structures
 */
export interface MedicationSchedule {
  times?: string[];               // Times of day for administration (e.g., "08:00")
  frequency?: number;             // How many times per period (e.g., 2 times per day)
  period?: number;                // Period length (e.g., 1 day)
  periodUnit?: 'h' | 'd' | 'wk' | 'mo'; // Period unit
  daysOfWeek?: string[];         // Days of week for administration
  instructions?: string;          // Text instructions for taking the medication
  doseQuantity?: number;          // Quantity per dose (e.g., 1 tablet)
  doseUnit?: string;              // Unit for the dose (e.g., "tablet(s)")
}

/**
 * Medication interface representing a user's medication
 * Enhanced with optional FHIR compatibility fields
 */
export interface Medication {
  id: string;
  userId?: string;                // User/patient ID for this medication
  name: string;
  form: MedicationForm;
  dosage: string;
  instructions: string;
  category: 'prescription' | 'otc' | 'supplement' | 'cycle-based' | 'fixed-duration' | 'daily';
  timing: 'morning' | 'afternoon' | 'evening' | 'bedtime' | 'multiple';
  time: 'morning' | 'afternoon' | 'evening' | 'bedtime';
  // Critical: This property maps time of day values to dose counts
  // Used to determine which time slots a medication appears in
  timeOfDayEntries?: Record<string, number>;
  
  // Display-formatted fields for consistent medication rendering
  formattedDosage?: string;      // Formatted dosage string (e.g., "10mg tablet")
  formattedInstructions?: string[];  // Array of formatted instruction strings
  travelAdjustment?: TravelAdjustment;
  strictTiming: boolean;
  maxTimeShift?: number;          // Maximum hours medication can be shifted
  cycle?: string;                 // Human-readable cycle description (e.g., "3 weeks on, 1 week off")
  cycleLength?: number;           // Total days in cycle
  activeLength?: number;          // Active days in cycle
  inactiveLength?: number;        // Inactive days in cycle
  cycleStart?: string;            // ISO date for the start of the first cycle
  importance?: 'high' | 'medium' | 'low'; // For visual hierarchy
  frequency?: {
    pattern: DosagePattern;
    customInterval?: number;
    cycleDays?: {
      on: number;
      off: number;
    };
    specificDays?: number[];      // Specific days of the month (1-31)
    daysOfWeek?: string[];       // Days of week (e.g., ['Monday', 'Wednesday', 'Friday'])
    startDate?: string;           // ISO date string for when the cycle starts
  };
  withFood: boolean;
  foodInstructions?: 'before' | 'with' | 'after';
  duration: string;
  color?: string;                 // Consistent color for this medication across the UI
  status?: 'active' | 'inactive' | 'taken' | 'missed';
  startDate?: string;             // ISO date string
  endDate?: string;               // ISO date string (optional for ongoing medications)
  taken: boolean;
  lastUpdated?: string;
  isPRN: boolean;
  notes?: string;
  createdAt?: Date;               // When the medication was first created
  updatedAt?: Date;               // When the medication was last updated
  
  // FHIR Compatibility Fields
  fhirMetadata?: FhirMetadata;    // FHIR-specific metadata
  schedule?: MedicationSchedule;  // FHIR-compatible schedule information
}

/**
 * Comprehensive medication interface for use across the application
 * Used for integration between medication creation dialog and medication display
 */
export interface WizardMedication {
  id?: string;
  name: string;
  form: MedicationForm;
  dosage: string;
  instructions?: string;
  // Extended category options to support MedicationScheduleContainer
  category?: 'prescription' | 'otc' | 'supplement' | 'cycle-based' | 'fixed-duration' | 'daily';
  timing?: string;
  time?: string;
  times?: {
    morning?: boolean;
    afternoon?: boolean;
    evening?: boolean;
    bedtime?: boolean;
  };
  strictTiming?: boolean;
  isPRN?: boolean;
  cycle?: string;
  frequency?: {
    pattern?: string;
    customInterval?: number;
    cycleDays?: {
      on: number;
      off: number;
    };
    specificDays?: number[];
    daysOfWeek?: string[];
    startDate?: string;
  };
  foodInstructions?: string;
  
  // Time of day integration fields
  timeOfDayEntries?: Record<string, number>;
  
  // Schedule fields
  daysOfWeek?: string[];
  
  // Note fields
  notes?: string;
  
  // Refill tracking fields
  refillTracking?: boolean;
  
  // Cycle tracking fields
  hasCycle?: boolean;
  cycleWeeksOn?: number;
  cycleWeeksOff?: number;
  cycleStart?: string;           // ISO date string for cycle start
  cycleLength?: number;          // Total length of cycle in days
  activeLength?: number;         // Active days in cycle
  
  // Custom medication object fields to support legacy components
  schedule?: {
    timing: string;
    frequency: string;
    daysOfWeek?: string[];
    custom?: string;
  };
}

export const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export type DayOfWeek = typeof daysOfWeek[number];

// Renamed to avoid conflict with the new MedicationSchedule interface
export type MedicationByDay = Record<DayOfWeek, Medication[]>;

/**
 * WeekDay interface for weekly view display
 */
export interface WeekDay {
  date: string;
  isToday: boolean;
  dayName: string;
  isPast: boolean;
  fhirDate?: string;              // ISO8601 date format for FHIR compatibility
}

/**
 * MedicationSummary interface for add/edit functionality
 * Simplified version of Medication with only editable fields
 */
/**
 * MedicationSummary interface for add/edit functionality
 * Simplified version of Medication with only editable fields
 * Enhanced with optional FHIR fields
 */
export interface MedicationSummary {
  id?: string;
  name: string;
  dosage: string;
  form: MedicationForm;
  instructions: string;
  time?: 'morning' | 'afternoon' | 'evening' | 'bedtime';
  daysOfWeek?: string[];
  category?: 'prescription' | 'otc' | 'supplement' | 'cycle-based' | 'fixed-duration' | 'daily';
  cycleStart?: string | number;
  cycleLength?: string | number;
  activeLength?: string | number;
  strictTiming?: boolean;
  
  // FHIR Compatibility Fields
  fhirMetadata?: FhirMetadata;    // Optional FHIR metadata
  schedule?: MedicationSchedule;  // FHIR-compatible schedule
}

export interface DemoPatient {
  id: string;
  name: string;
  description: string;
  medications: Medication[]; // Changed to use the new Medication interface
}